import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, Check, X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Notification } from '@/types/dashboard';
import { NotificationIcon } from './NotificationIcon';
import { NotificationTypeBadge } from './NotificationTypeBadge';
import { formatTimeAgo } from './formatTimeAgo';

interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

interface NotificationListProps {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  onRefetch: () => void;
  onMarkAsRead: (notification: Notification) => void;
  onMarkAsUnread: (notification: Notification) => void;
  onDelete: (notification: Notification) => void;
  onPageChange?: (page: number) => void;
}

function getPageFromUrl(url: string | null): number | null {
  if (!url) return null;
  const match = url.match(/[?&]page=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

export function NotificationList({
  notifications,
  loading,
  error,
  pagination,
  onRefetch,
  onMarkAsRead,
  onMarkAsUnread,
  onDelete,
  onPageChange,
}: NotificationListProps) {
  const currentPage = pagination?.next
    ? (getPageFromUrl(pagination.next) ?? 2) - 1
    : pagination?.previous
      ? (getPageFromUrl(pagination.previous) ?? 0) + 1
      : 1;
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>All Notifications ({pagination?.count ?? notifications.length})</CardTitle>
            <CardDescription>
              {unreadCount > 0 && `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-start gap-4 p-4">
                <Skeleton className="h-7 w-7 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bell className="h-12 w-12 text-destructive mb-4" />
            <p className="text-lg font-medium text-destructive mb-2">Failed to load notifications</p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={onRefetch} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Bell className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No notifications found</p>
            <p className="text-sm text-muted-foreground mt-1">You're all caught up!</p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {notifications.map((notification, index) => (
                <div
                  key={`${notification.timestamp}-${index}`}
                  className={`p-4 rounded-lg border transition-all ${
                    !notification.read
                      ? 'bg-primary/5 border-primary/20'
                      : 'bg-card border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1 shrink-0">
                      <NotificationIcon type={notification.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p
                              className={`text-sm font-medium ${
                                !notification.read ? 'text-foreground' : 'text-muted-foreground'
                              }`}
                            >
                              {notification.title}
                            </p>
                            <NotificationTypeBadge type={notification.type} />
                            {!notification.read && (
                              <Badge variant="secondary" className="text-xs">
                                New
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2 flex-wrap">
                            <span>{formatTimeAgo(notification.timestamp)}</span>
                            <span>•</span>
                            <span>{new Date(notification.timestamp).toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {!notification.read ? (
                            <Button
                              onClick={() => onMarkAsRead(notification)}
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              aria-label="Mark as read"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              onClick={() => onMarkAsUnread(notification)}
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              aria-label="Mark as unread"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            onClick={() => onDelete(notification)}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {pagination && pagination.count > 0 && onPageChange && (
              <div className="flex items-center justify-between border-t pt-4 mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {notifications.length} of {pagination.count} notifications
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={!pagination.previous || loading}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">Page {currentPage}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={!pagination.next || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
